import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import {
  DISPATCH_TEMPLATE_OPTIONS,
  type ProjectTemplateRecipientType,
} from '../../config/templateConfig'

interface DispatchTemplateSelectorModalProps {
  onSelect: (templateType: ProjectTemplateRecipientType) => void
  onClose: () => void
}

const DispatchTemplateSelectorModal: React.FC<
  DispatchTemplateSelectorModalProps
> = ({ onSelect, onClose }) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className={css(styles.backdrop)}
      onClick={handleBackdropClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleBackdropClick(e as unknown as React.MouseEvent)
        }
      }}
      // biome-ignore lint/a11y/useSemanticElements: backdrop overlay click handler
      role="button"
      tabIndex={0}
    >
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>Select Dispatch Type</h2>
          <button
            className={css(styles.closeButton)}
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <i className="fa-solid fa-times" />
          </button>
        </div>

        <div className={css(styles.content)}>
          <p className={css(styles.description)}>
            Choose the type of dispatch you want to create:
          </p>

          <div className={css(styles.optionsContainer)}>
            {DISPATCH_TEMPLATE_OPTIONS.map((option) => (
              <button
                key={option.value}
                className={css(styles.optionButton)}
                onClick={() => onSelect(option.value)}
                type="button"
              >
                <div className={css(styles.optionIcon)}>
                  {option.value === 'BROKER' && (
                    <i className="fa-solid fa-handshake" />
                  )}
                  {option.value === 'THIRD_PARTY_CONTRACTOR' && (
                    <i className="fa-solid fa-building" />
                  )}
                  {option.value === 'SUB_HAULER' && (
                    <i className="fa-solid fa-truck" />
                  )}
                </div>
                <span className={css(styles.optionLabel)}>{option.label}</span>
                <i
                  className={`fa-solid fa-chevron-right ${css(styles.optionArrow)}`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className={css(styles.footer)}>
          <button
            className={css(styles.cancelButton)}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  closeButton: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#6B7280',
    fontSize: '16px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#111827',
    },
  },
  content: {
    padding: '24px',
  },
  description: {
    margin: '0 0 20px 0',
    fontSize: '14px',
    color: '#6B7280',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  optionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: '#3B82F6',
      backgroundColor: '#F0F7FF',
    },
  },
  optionIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: '#EFF6FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#3B82F6',
    fontSize: '18px',
  },
  optionLabel: {
    flex: 1,
    fontSize: '15px',
    fontWeight: 500,
    color: '#111827',
  },
  optionArrow: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #E5E7EB',
    backgroundColor: '#F9FAFB',
  },
  cancelButton: {
    padding: '10px 20px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
})

export default DispatchTemplateSelectorModal
